import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { getMyDashboard, bookAppointment } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CalendarIcon, Loader2, ScanFace, ShoppingBag, CalendarCheck, FileText } from "lucide-react";
import { toast } from "sonner";

const TIME_SLOTS = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "13:00",
  "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"];
const ALL_SERVICES = ["CO2 Fractional Laser", "IPL Photofacial", "Wet Cupping",
  "Dry Cupping", "Moving Cupping", "PRP Therapy", "IV Hydration Therapy",
  "Weight Management", "Mesotherapy", "HIFU Skin Lifting", "Free Consultation"];

const statusStyle = (s) => ({
  pending: "bg-[#E4CE8F]/40 text-[#7a5c12]",
  confirmed: "bg-[#3E5C4E]/15 text-[#2f4a3d]",
  completed: "bg-[#2B2340]/10 text-[#2B2340]",
  cancelled: "bg-[#8C3B3B]/10 text-[#8C3B3B]",
}[s] || "bg-gray-100 text-gray-600");

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [svc, setSvc] = useState("");
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [booking, setBooking] = useState(false);
  const isWet = svc.toLowerCase().includes("wet");

  const load = () => getMyDashboard().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const book = async (e) => {
    e.preventDefault();
    if (!svc || !date || !time) { toast.error("Pick a service, date and time."); return; }
    if (isWet && !smsConsent) { toast.error("Please agree to receive SMS instructions for your Wet Cupping appointment."); return; }
    setBooking(true);
    try {
      await bookAppointment({ service: svc, date: format(date, "yyyy-MM-dd"), time, notes });
      toast.success("Appointment requested");
      setSvc(""); setDate(undefined); setTime(""); setNotes(""); setSmsConsent(false);
      load();
    } catch {
      toast.error("Could not book appointment.");
    } finally {
      setBooking(false);
    }
  };

  if (!data)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9A7420]" /></div>;

  return (
    <main className="pt-28 pb-28 min-h-screen relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="overline text-[#9A7420] mb-3">Your Space</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mb-2">Hello, {user?.name?.split(" ")[0]}</h1>
        <p className="text-[#57515F] mb-12">Track your skin journey, appointments and orders.</p>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Booking + quick actions */}
          <div className="lg:col-span-1 space-y-8">
            <Reveal>
              <div className="bg-white border border-[#DAD5E2] rounded-2xl p-7">
                <h3 className="font-display text-2xl mb-5 flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-[#9A7420]" /> Book an appointment</h3>
                <form data-testid="book-form" onSubmit={book} className="space-y-5">
                  <div>
                    <label className="overline block mb-2">Service</label>
                    <Select value={svc} onValueChange={setSvc}>
                      <SelectTrigger data-testid="book-service" className="input-line h-auto"><SelectValue placeholder="Select service" /></SelectTrigger>
                      <SelectContent>{ALL_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline block mb-2">Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="book-date" className="input-line flex items-center justify-between text-left">
                          {date ? format(date, "PPP") : <span className="text-[#8B8494]">Pick a date</span>}
                          <CalendarIcon className="w-4 h-4 text-[#8B8494]" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="overline block mb-2">Time</label>
                    <Select value={time} onValueChange={setTime}>
                      <SelectTrigger data-testid="book-time" className="input-line h-auto"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent className="max-h-60">{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline block mb-2">Notes (optional)</label>
                    <input data-testid="book-notes" className="input-line" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                  {isWet && (
                    <label data-testid="sms-consent-block" className="flex gap-3 items-start text-xs text-[#57515F] bg-[#F8F5EE] border border-[#E4CE8F]/60 rounded-xl p-4 cursor-pointer">
                      <input data-testid="sms-consent" type="checkbox" checked={smsConsent}
                        onChange={(e) => setSmsConsent(e.target.checked)} className="mt-0.5 accent-[#9A7420]" />
                      <span>
                        I agree to receive up to <b>2 SMS messages per appointment</b> from Women Heaven with Wet Cupping pre-care and after-care instructions at the phone number on my account. Msg &amp; data rates may apply. Reply STOP to opt out. See our <Link to="/terms" className="underline text-[#9A7420]">Terms</Link> and <Link to="/privacy" className="underline text-[#9A7420]">Privacy Policy</Link>.
                      </span>
                    </label>
                  )}
                  <button data-testid="book-submit" type="submit" disabled={booking} className="btn-primary w-full justify-center">
                    {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Appointment"}
                  </button>
                </form>
              </div>
            </Reveal>

            <div className="flex gap-4">
              <Link to="/skin-analysis" data-testid="dash-analysis" className="btn-secondary flex-1 justify-center text-sm"><ScanFace className="w-4 h-4" /> Skin Scan</Link>
              <Link to="/shop" data-testid="dash-shop" className="btn-secondary flex-1 justify-center text-sm"><ShoppingBag className="w-4 h-4" /> Shop</Link>
            </div>
          </div>

          {/* Right: Timeline */}
          <div className="lg:col-span-2 space-y-10">
            {/* Appointments */}
            <section>
              <h3 className="font-display text-3xl mb-5">Appointments</h3>
              {data.appointments.length === 0 ? (
                <p className="text-[#8B8494]">No appointments yet.</p>
              ) : (
                <div className="space-y-3" data-testid="dash-appointments">
                  {data.appointments.map((a) => (
                    <div key={a.id} className="bg-white border border-[#DAD5E2] rounded-xl p-5 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{a.service}</p>
                        <p className="text-sm text-[#57515F]">{a.date} · {a.time}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusStyle(a.status)}`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Skin analysis timeline */}
            <section>
              <h3 className="font-display text-3xl mb-5">Skin Analysis History</h3>
              {data.analyses.length === 0 ? (
                <p className="text-[#8B8494]">No scans yet. <Link to="/skin-analysis" className="text-[#9A7420] underline">Run your first AI skin scan</Link>.</p>
              ) : (
                <div className="space-y-4" data-testid="dash-analyses">
                  {data.analyses.map((an) => (
                    <div key={an.id} className="bg-white border border-[#DAD5E2] rounded-xl p-5 flex gap-5">
                      {an.image && <img src={an.image} alt="selfie" className="w-20 h-24 object-cover rounded-lg flex-shrink-0" />}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-display text-2xl">{an.result?.overall_score}<span className="text-sm text-[#8B8494]">/100</span></span>
                          <span className="text-xs text-[#8B8494]">{an.created_at?.slice(0, 10)}</span>
                        </div>
                        <p className="text-sm text-[#57515F] mt-1">{an.result?.skin_type} · {an.result?.summary?.slice(0, 90)}…</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Treatment notes */}
            {data.notes.length > 0 && (
              <section data-testid="dash-notes">
                <h3 className="font-display text-3xl mb-5 flex items-center gap-2"><FileText className="w-5 h-5 text-[#9A7420]" /> Treatment Notes</h3>
                <div className="space-y-3">
                  {data.notes.map((n) => (
                    <div key={n.id} className="bg-[#E4E0EB] border border-[#DAD5E2] rounded-xl p-4">
                      <p className="text-[#2B2340]">{n.note}</p>
                      <p className="text-xs text-[#8B8494] mt-1">{n.created_at?.slice(0, 10)}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Payments */}
            <section>
              <h3 className="font-display text-3xl mb-5">Orders</h3>
              {data.payments.length === 0 ? (
                <p className="text-[#8B8494]">No orders yet.</p>
              ) : (
                <div className="space-y-3" data-testid="dash-payments">
                  {data.payments.map((p) => (
                    <div key={p.session_id} className="bg-white border border-[#DAD5E2] rounded-xl p-4 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{p.product_name || "Order"}</p>
                        <p className="text-xs text-[#8B8494]">{p.created_at?.slice(0, 10)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${Number(p.amount).toFixed(2)}</p>
                        <span className={`text-xs capitalize ${p.payment_status === "paid" ? "text-[#3E5C4E]" : "text-[#8B8494]"}`}>{p.payment_status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
